import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Button,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  Forward as ForwardIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { Email, ComposeContext } from '../../types/email';
import { API_BASE_URL, FILE_BASE_URL } from '../../config';
import { getFileUrl } from '../../utils/urlHelper';
import axios from 'axios';
import DOMPurify from 'dompurify';

// Funciones de utilidad temporales (se moverán a utils después)
const extractCidsFromHtml = (html: string): string[] => {
  if (!html) return [];
  const cidRegex = /src=["']cid:([^"']+)["']/gi;
  const cids: string[] = [];
  let match;
  while ((match = cidRegex.exec(html)) !== null) {
    cids.push(match[1]);
  }
  return cids;
};

const replaceCidSrc = (html: string, email: Email, detalle?: any): string => {
  if (!html || !email) return html;
  
  let newHtml = html;
  const cidsEncontrados = extractCidsFromHtml(html);
  
  if (cidsEncontrados.length > 0) {
    console.log('[DEBUG] CIDs encontrados en HTML:', cidsEncontrados);
    
    // Usar SOLO inline_attachments del detalle
    const attachments = detalle?.inline_attachments || [];
    console.log('[DEBUG] Attachments inline disponibles:', attachments?.map((a: any) => ({id: a.id, name: a.name, contentId: a.contentId})));
    
    // Reemplazar cada cid por su URL correspondiente
    newHtml = newHtml.replace(/src=["']cid:([^"']+)["']/gi, (match, cid) => {
      console.log(`[DEBUG] Procesando CID: "${cid}"`);
      
      // Normalizar el CID encontrado en el HTML
      const cidNorm = cid.replace(/[<>\s]/g, '').toLowerCase();
      console.log(`[DEBUG] CID normalizado: "${cidNorm}"`);
      
      // Buscar attachment por contentId o por name (normalizados)
      const att = attachments?.find((a: any) => {
        const attContentId = (a.contentId || '').replace(/[<>\s]/g, '').toLowerCase();
        const attName = (a.name || '').replace(/[<>\s]/g, '').toLowerCase();
        
        console.log(`[DEBUG] Comparando con attachment: contentId="${attContentId}", name="${attName}"`);
        
        const matchByContentId = attContentId === cidNorm;
        const matchByName = attName === cidNorm;
        
        if (matchByContentId || matchByName) {
          console.log(`[DEBUG] ¡MATCH ENCONTRADO! Attachment ID: ${a.id}`);
        }
        
        return matchByContentId || matchByName;
      });
      
      if (att) {
        // Usar token temporal si está disponible, si no, usar URL normal
        const token = att.access_token;
        const backendUrl = token 
          ? `${API_BASE_URL}/email-attachment/${email.id}/${att.id}?token=${token}`
          : `${API_BASE_URL}/email-attachment/${email.id}/${att.id}`;
        console.log(`[DEBUG] Reemplazando CID con URL: ${backendUrl}`);
        return `src="${backendUrl}" style="max-width: 100%; height: auto;"`;
      }
      
      console.log(`[DEBUG] No se encontró attachment para CID: "${cid}"`);
      // Si no se encuentra el attachment, reemplazar por mensaje amigable
      return `src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200' preserveAspectRatio='xMidYMid meet'%3E%3Crect width='100%25' height='100%25' fill='%23f5f5f5' stroke='%23ccc' stroke-width='1'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' fill='%23888' font-style='italic' font-size='14' font-family='Arial, sans-serif'%3E%3Ctspan x='50%25' dy='-0.5em'%3ELa imagen no se%3C/tspan%3E%3Ctspan x='50%25' dy='1.2em'%3Eprocesó correctamente%3C/tspan%3E%3C/text%3E%3C/svg%3E" style="max-width: 300px; max-height: 200px; width: auto; height: auto;"`;
    });
  }
  
  return newHtml;
};

const processEmailBodyForDisplay = (email: Email, detalle?: any): string => {
  if (!email) return '';
  
  // Obtener el HTML del email
  const html = email.bodyHtml || email.bodyRawHtml || email.body || '';
  
  // Sanitizar y procesar CIDs
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: ['style'], // Permitir el atributo style
  });
  const processedHtml = replaceCidSrc(sanitizedHtml, email, detalle);
  
  return processedHtml;
};

export interface EmailPreviewProps {
  email: Email | null;
  onCompose: (context: ComposeContext) => void;
  onSetFullEmail: (fullEmail: Email) => void;
  loading?: boolean;
  paciente?: any | null;
  updateEmailFlag?: (emailId: string, flagged: boolean) => void;
  updateEmailReadStatus?: (emailId: string, isRead: boolean) => void;
  onDeleteEmail?: (emailId: string) => void;
  onRestoreEmail?: (emailId: string) => void;
  currentFolder?: string;
  onContentLoaded?: () => void;
}

// Función para verificar si hay contenido en el email
const hasContent = (email: Email): boolean => {
  return !!(email.body || email.bodyHtml || email.bodyRawHtml);
};

// Función para verificar si hay adjuntos
const hasAttachments = (email: Email): boolean => {
  return email.hasAttachments && Array.isArray(email.attachments) && email.attachments.length > 0;
};

// Función para verificar si hay CIDs sin adjuntos correspondientes
const hasUnresolvedCids = (email: Email, detalle?: any): boolean => {
  if (!email.bodyHtml && !email.bodyRawHtml) return false;
  
  const html = email.bodyHtml || email.bodyRawHtml || '';
  const cids = extractCidsFromHtml(html);
  
  if (cids.length === 0) return false;
  
  // Usar SOLO inline_attachments del detalle
  const attachments = detalle?.inline_attachments || [];
  
  // Verificar si todos los CIDs tienen adjuntos correspondientes
  return cids.some((cid: string) => {
    const cidNorm = cid.replace(/[<>\s]/g, '').toLowerCase();
    return !attachments?.some((att: any) => {
      const attContentId = (att.contentId || '').replace(/[<>\s]/g, '').toLowerCase();
      const attName = (att.name || '').replace(/[<>\s]/g, '').toLowerCase();
      return attContentId === cidNorm || attName === cidNorm;
    });
  });
};

const EmailPreview: React.FC<EmailPreviewProps> = ({ email, onCompose, onSetFullEmail, loading = false, paciente, updateEmailFlag, updateEmailReadStatus, onDeleteEmail, onRestoreEmail, currentFolder, onContentLoaded }) => {
  // console.debug('[DEBUG] EmailPreview props:', { email, updateEmailFlag });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detalle, setDetalle] = useState<any>(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [readStatus, setReadStatus] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  
  // Referencias para cancelación de peticiones
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentRequestIdRef = useRef<number | null>(null);

  // Resetear contentLoaded cuando cambia el email
  useEffect(() => {
    setContentLoaded(false);
  }, [email?.id]);

  // Controlar cuando el contenido está completamente cargado
  useEffect(() => {
    if (email && !loadingDetalle && (detalle || !email.hasAttachments)) {
      // El contenido está cargado si:
      // 1. Tenemos un email
      // 2. No está cargando el detalle
      // 3. Tenemos el detalle O el email no tiene adjuntos (no necesita cargar detalle)
      setContentLoaded(true);
      if (onContentLoaded) {
        onContentLoaded();
      }
    }
  }, [email, loadingDetalle, detalle, onContentLoaded]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para obtener la fecha correcta según el tipo de email
  const getEmailDate = (email: Email) => {
    // Para borradores, usar lastModifiedDateTime si receivedDateTime no está disponible
    if (email.folder === 'borradores' || !email.receivedDateTime) {
      return email.lastModifiedDateTime || email.receivedDateTime || new Date().toISOString();
    }
    return email.receivedDateTime;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleReply = () => {
    // console.log('[DEBUG] handleReply - detalle disponible:', detalle);
    // console.log('[DEBUG] handleReply - loadingDetalle:', loadingDetalle);
    // console.log('[DEBUG] handleReply - email folder:', email?.folder);
    // console.log('[DEBUG] handleReply - email from:', email?.from);
    // console.log('[DEBUG] handleReply - email to:', email?.to);
    // console.log('[DEBUG] handleReply - email cc:', email?.cc);
    if (!detalle) {
      // console.warn('[DEBUG] handleReply - detalle no disponible, esperando carga...');
      return;
    }
    if (email) onCompose({ mode: 'reply', email, emailDetails: detalle });
  };

  // Función para determinar si mostrar "Responder a todos"
  const shouldShowReplyAll = (email: Email): boolean => {
    const toRecipients = email.to || [];
    const ccRecipients = email.cc || [];
    
    // Mostrar si hay más de 1 destinatario en "Para" O hay destinatarios en "CC"
    return toRecipients.length > 1 || ccRecipients.length > 0;
  };

  const handleReplyAll = () => {
    // console.log('[DEBUG] handleReplyAll - detalle disponible:', detalle);
    // console.log('[DEBUG] handleReplyAll - loadingDetalle:', loadingDetalle);
    if (!detalle) {
      // console.warn('[DEBUG] handleReplyAll - detalle no disponible, esperando carga...');
      return;
    }
    if (email) onCompose({ mode: 'reply-all', email, emailDetails: detalle });
  };

  const handleForward = () => {
    // console.log('[DEBUG] handleForward - detalle disponible:', detalle);
    // console.log('[DEBUG] handleForward - loadingDetalle:', loadingDetalle);
    if (!detalle) {
      // console.warn('[DEBUG] handleForward - detalle no disponible, esperando carga...');
      return;
    }
    if (email) onCompose({ mode: 'forward', email, emailDetails: detalle });
  };

  const handleAIGenerate = () => {
    // Abrir el modal de composición con el contexto de AI
    if (email) {
      const context: ComposeContext = {
        mode: 'reply',
        email: email,
        emailDetails: detalle,
        // Marcar que queremos abrir la configuración de AI
        originalMode: 'ai-generate'
      };
      onCompose(context);
    }
  };

  const handleEdit = () => {
    if (email) {
      onCompose({ 
        originalMode: 'edit-draft', 
        mode: 'new', 
        email,
        emailDetails: detalle // Agregar los detalles con inline_attachments
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!email) return;
    
    setDeleting(true);
    try {
      // Usar la función correcta según la carpeta
      if (currentFolder === 'papelera' && onRestoreEmail) {
        onRestoreEmail(email.id);
      } else if (onDeleteEmail) {
        onDeleteEmail(email.id);
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error en la acción:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    setFlagged(email?.isFlagged ?? false);
  }, [email?.isFlagged, email?.id]);

  useEffect(() => {
    setReadStatus(email?.isRead ?? false);
  }, [email?.isRead, email?.id]);

  const handleMarkAsRead = async () => {
    if (!email) return;
    
    setMarkingAsRead(true);
    try {
      const response = await fetch(`${API_BASE_URL}/marcar-leido`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message_id: email.id,
          is_read: !readStatus
        })
      });

      if (response.ok) {
        setReadStatus(!readStatus);
        // Actualizar el email en el estado padre si existe la función
        if (onSetFullEmail) {
          onSetFullEmail({
            ...email,
            isRead: !readStatus
          });
        }
        // Actualizar el estado de lectura en la lista de emails
        if (updateEmailReadStatus) {
          updateEmailReadStatus(email.id, !readStatus);
        }
      } else {
        console.error('Error al marcar como leído');
      }
    } catch (error) {
      console.error('Error al marcar como leído:', error);
    } finally {
      setMarkingAsRead(false);
    }
  };

  useEffect(() => {
    if (email && email.id) {
      // Cancelar petición anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Crear nuevo AbortController y Request ID
      abortControllerRef.current = new AbortController();
      const requestId = Date.now();
      currentRequestIdRef.current = requestId;
      
      const fetchDetalle = async () => {
        setLoadingDetalle(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/email-detalle/${email.id}`, {
            signal: abortControllerRef.current?.signal
          });
          
          // Verificar si la petición sigue siendo la actual
          if (currentRequestIdRef.current === requestId) {
            // console.log('[DEBUG] EmailPreview - emailDetails cargados:', response.data);
            setDetalle(response.data);
            
            // --- ¡LA CIRUGÍA CLAVE! ---
            // Unificar el email base con el detalle obtenido
            const fullEmail = { ...email, ...response.data };
            // Notificar al padre que tenemos el email completo
            onSetFullEmail(fullEmail);
            
            if (onContentLoaded) onContentLoaded();
          }
        } catch (error: any) {
          if (error.name !== 'CanceledError') {
            console.error('Error fetching email details:', error);
          }
        } finally {
          if (currentRequestIdRef.current === requestId) {
            setLoadingDetalle(false);
          }
        }
      };

      fetchDetalle();

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }
  }, [email?.id]); // Dependencia clave

  const handleStar = async () => {
    if (!email) return;
    const nuevoValor = !flagged;
    setFlagged(nuevoValor); // Optimista
    try {
      await fetch(`${API_BASE_URL}/marcar-flag`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message_id: email.id, flagged: nuevoValor })
      });
      if (updateEmailFlag) {
        console.debug('[DEBUG] Llamando a updateEmailFlag desde EmailPreview', email.id, nuevoValor);
        updateEmailFlag(email.id, nuevoValor);
      }
    } catch (e) {
      setFlagged(!nuevoValor); // Revertir si falla
    }
  };

  const noPaciente = !email && (!paciente || !paciente.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (noPaciente) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'flex-start',
        minHeight: 320,
        p: 3
      }}>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 8 }}>
          Selecciona un paciente
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Primero selecciona un paciente para ver sus emails.
        </Typography>
      </Box>
    );
  }

  if (!email) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'flex-start',
        minHeight: 320,
        p: 3
      }}>
        <Typography variant="h5" color="text.secondary" gutterBottom sx={{ mt: 8 }}>
          Selecciona un email
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Elige un email de la conversación para ver su contenido
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onCompose({ mode: 'new', email: null })}
          sx={{ mt: 2 }}
        >
          Nuevo Email
        </Button>
      </Box>
    );
  }

  // Determinar qué contenido mostrar
  const hasEmailContent = email && (email.body || detalle?.body || email.bodyPreview);
  const hasEmailAttachments = email && (
    (email.attachments ?? []).length > 0 || 
    (detalle?.inline_attachments ?? []).length > 0
  );
  const hasUnresolvedCidImages = hasUnresolvedCids(email, detalle);
  const hasAnyContent = hasEmailContent || hasEmailAttachments;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
      {/* Header del email */}
      <Paper elevation={0} sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        {/* Barra de herramientas */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {email.subject}
            </Typography>
            {/* Indicador de borrador en historial completo */}
            {(currentFolder === 'todos' && email.folder === 'borradores') || currentFolder === 'borradores' ? (
              <Chip 
                label="Borrador" 
                size="small" 
                color="warning" 
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={!contentLoaded ? "Cargando contenido..." : (readStatus ? "Marcar como no leído" : "Marcar como leído")}>
              <IconButton
                onClick={handleMarkAsRead}
                disabled={markingAsRead || !contentLoaded}
              >
                {markingAsRead ? (
                  <CircularProgress size={20} />
                ) : readStatus ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={!contentLoaded ? "Cargando contenido..." : "Marcar como importante"}>
              <IconButton onClick={handleStar} disabled={!contentLoaded}>
                {flagged ? (
                  <StarIcon color="warning" />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title={!contentLoaded ? "Cargando contenido..." : (currentFolder === 'papelera' ? "Restaurar" : "Eliminar")}>
              <IconButton onClick={handleDelete} color={currentFolder === 'papelera' ? 'primary' : 'error'} disabled={!contentLoaded}>
                {currentFolder === 'papelera' ? <RestoreIcon /> : <DeleteIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Información del remitente */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={['enviados', 'borradores'].includes(currentFolder || '') || (['papelera', 'spam'].includes(currentFolder || '') && email.from?.email === 'drduran@hotmail.com.ar') || (currentFolder === 'todos' && email.folder === 'enviados') ? undefined : getFileUrl(paciente?.ruta_imagen_perfil)}
            sx={{ 
              width: 48, 
              height: 48, 
              mr: 2,
              bgcolor: ['enviados', 'borradores'].includes(currentFolder || '') || (currentFolder === 'todos' && email.folder === 'enviados') 
                ? (email.isRead ? 'grey.400' : 'primary.main')
                : (email.isRead ? 'grey.400' : 'primary.main')
            }}
          >
            {['enviados', 'borradores'].includes(currentFolder || '') || (['papelera', 'spam'].includes(currentFolder || '') && email.from?.email === 'drduran@hotmail.com.ar') || (currentFolder === 'todos' && email.folder === 'enviados') ? 'JC' : (paciente?.nombre ? paciente.nombre[0].toUpperCase() : getInitials(email.from.name))}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {email.from.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {email.from.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(getEmailDate(email))}
            </Typography>
          </Box>
        </Box>

        {/* Destinatarios */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Para: {email.to.map(recipient => recipient.name).join(', ')}
          </Typography>
          {email.cc && email.cc.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              CC: {email.cc.map(recipient => recipient.name).join(', ')}
            </Typography>
          )}
        </Box>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(currentFolder === 'borradores' || (currentFolder === 'todos' && email.folder === 'borradores')) ? (
            // Botones para borradores
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                size="small"
                disabled={!contentLoaded}
              >
                Editar
              </Button>
            </>
          ) : (
            // Botones para emails normales
            <>
              <Button
                variant="outlined"
                startIcon={<ReplyIcon />}
                onClick={handleReply}
                size="small"
                disabled={!contentLoaded}
              >
                {(currentFolder === 'enviados' || (currentFolder === 'todos' && email.folder === 'enviados')) ? 'Responder al Paciente' : 'Responder'}
              </Button>
              {shouldShowReplyAll(email) && (
                <Button
                  variant="outlined"
                  startIcon={<ReplyAllIcon />}
                  onClick={handleReplyAll}
                  size="small"
                  disabled={!contentLoaded}
                >
                  Responder a todos
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<ForwardIcon />}
                onClick={handleForward}
                size="small"
                disabled={!contentLoaded}
              >
                Reenviar
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Contenido del email */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Indicador de carga de contenido */}
        {!contentLoaded && email && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: 2,
            p: 1,
            bgcolor: 'info.50',
            borderRadius: 1,
            border: 1,
            borderColor: 'info.200'
          }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="info.main">
              Cargando contenido del email...
            </Typography>
          </Box>
        )}

        {/* Adjuntos - SIEMPRE mostrar si hasAttachments es true */}
        {email.hasAttachments && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            {!hasEmailContent && hasEmailAttachments && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Este email no tiene texto, pero contiene los siguientes archivos adjuntos:
              </Typography>
            )}
            
            {hasEmailAttachments ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Adjuntos ({((email.attachments?.length || 0) + (detalle?.inline_attachments?.length || 0))})
                </Typography>
                <List dense>
                  {/* Mostrar attachments regulares */}
                  {email.attachments?.map((attachment) => (
                    <ListItem key={attachment.id} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AttachFileIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.name}
                        secondary={`${(attachment.size / 1024).toFixed(1)} KB`}
                      />
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          // Buscar si hay un token para este attachment en regular_attachments
                          const regularAttachment = detalle?.regular_attachments?.find((a: any) => a.id === attachment.id);
                          const token = regularAttachment?.access_token;
                          const url = token 
                            ? `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}?token=${token}`
                            : `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}`;
                          window.open(url, '_blank');
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                  
                  {/* Mostrar inline attachments (imágenes pegadas) */}
                  {detalle?.inline_attachments?.map((attachment: any) => (
                    <ListItem key={attachment.id} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <VisibilityIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${attachment.name} (imagen inline)`}
                        secondary={`${(attachment.size / 1024).toFixed(1)} KB`}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const token = attachment.access_token;
                          const url = token 
                            ? `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}?token=${token}`
                            : `${API_BASE_URL}/email-attachment/${email.id}/${attachment.id}`;
                          window.open(url, '_blank');
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay archivos adjuntos
              </Typography>
            )}
          </Paper>
        )}

        {/* Cuerpo del email */}
        {loadingDetalle ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
            <CircularProgress size={32} />
          </Box>
        ) : hasEmailContent ? (
          <Box sx={{ 
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider'
          }}>
            {detalle?.bodyHtml && detalle?.contentType === 'html' ? (
                  <div 
                    dangerouslySetInnerHTML={{ 
                  __html: processEmailBodyForDisplay(email, detalle)
                }}
                style={{ 
                  fontFamily: 'inherit',
                  lineHeight: 1.6,
                  wordWrap: 'break-word'
                }}
                className="email-content"
              />
            ) : (
              <Typography 
                variant="body1" 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  lineHeight: 1.6,
                  wordWrap: 'break-word'
                }}
              >
                {detalle?.body || email.body || email.bodyPreview || 'Sin contenido'}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: 120,
            bgcolor: 'grey.50',
            borderRadius: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Este email no tiene contenido de texto
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Diálogo de confirmación para eliminar/restaurar */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>
          {currentFolder === 'papelera' ? 'Confirmar restauración' : 'Confirmar eliminación'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {currentFolder === 'papelera' 
              ? `¿Estás seguro de que quieres restaurar el email "${email?.subject}"?`
              : `¿Estás seguro de que quieres eliminar el email "${email?.subject}"?`
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {currentFolder === 'papelera'
              ? 'El email se moverá de vuelta a la bandeja de entrada.'
              : 'El email se moverá a la papelera. Puedes recuperarlo desde allí si es necesario.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color={currentFolder === 'papelera' ? 'primary' : 'error'}
            variant="contained"
            disabled={deleting}
          >
            {deleting 
              ? (currentFolder === 'papelera' ? 'Restaurando...' : 'Eliminando...') 
              : (currentFolder === 'papelera' ? 'Restaurar' : 'Eliminar')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailPreview; 